import React from 'react';
import { Box, Paper, Skeleton, type SxProps, type Theme, Typography } from '@mui/material';

import { SectionLoadingSkeleton } from './SectionLoadingSkeleton';
import type { TemplateSection } from './types';

interface SectionItemPresentationProps {
  /** Section data to display */
  section: TemplateSection;

  /** Optional drag handle slot (left side) */
  dragHandleSlot?: React.ReactNode;

  /** Optional actions slot (right side, typically remove button) */
  actionsSlot?: React.ReactNode;

  /** Paper elevation (default: 1) */
  elevation?: number;

  /** Custom styles for Paper */
  sx?: SxProps<Theme>;

  /** Ref for drag-and-drop (optional) */
  paperRef?: React.Ref<HTMLDivElement>;

  /** Inline styles for drag-and-drop (optional) */
  style?: React.CSSProperties;
}

/**
 * Pure presentation component for displaying a template section
 * Uses slots pattern for flexible composition
 */
export const SectionItemPresentation: React.FC<SectionItemPresentationProps> = ({
  section,
  dragHandleSlot,
  actionsSlot,
  elevation = 1,
  sx,
  paperRef,
  style,
}) => {
  return (
    <Paper
      ref={paperRef}
      style={style}
      elevation={elevation}
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        display: 'flex',
        ...sx,
      }}
    >
      {/* Drag Handle Slot */}
      {dragHandleSlot && (
        <Box
          sx={{
            width: 40,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: 'action.hover',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {dragHandleSlot}
        </Box>
      )}

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          p: 2,
          gap: 2,
          flex: 1,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Section Title */}
          {section.title ? (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: 'text.primary',
              }}
            >
              {section.title}
            </Typography>
          ) : (
            <Skeleton
              variant="text"
              width="40%"
              height={32}
              animation="wave"
              sx={{
                bgcolor: 'rgba(0, 0, 0, 0.06)',
                borderRadius: 0.5,
                mb: 1,
              }}
            />
          )}

          {/* Section Content */}
          {section.content ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {section.content}
            </Typography>
          ) : (
            <SectionLoadingSkeleton />
          )}
        </Box>

        {/* Actions Slot */}
        {actionsSlot && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {actionsSlot}
          </Box>
        )}
      </Box>
    </Paper>
  );
};
